






































































































import {Component, Prop, Vue} from 'vue-property-decorator'
import {$} from '@/facade'
import {OrbitRoundReward} from '@/model/resource/OrbitRoundReward'
import {OrbitRoundRewardCollection} from '@/model/collection/OrbitRoundRewardCollection'
import {getN3InvocationBuilder} from '@/model/wallets/invocationsBuilder'
import {OrbitDistributeRewardStep} from '@/enums/OrbitDistributeRewardStep'
import {NeoHelper} from '@/helpers/NeoHelper'
import OrbitLoadingTransactionStep from '@/components/orbit/OrbitLoadingTransactionStep.vue'
import OrbitSuccessStep from '@/components/orbit/OrbitSuccessStep.vue'
import OrbitInsufficientGasStep from '@/components/orbit/OrbitInsufficientGasStep.vue'
import OrbitUnexpectedErrorStep from '@/components/orbit/OrbitUnexpectedErrorStep.vue'
import OrbitRejectedByTheUserStep from '@/components/orbit/OrbitRejectedByTheUserStep.vue'
import {OrbitHelper} from '@/helpers/OrbitHelper'
import {DateHelper} from '@/helpers/DateHelper'
import {OrbitRoundCollection} from '@/model/collection/OrbitRoundCollection'
import {currentN3Network} from '@/helpers/NetworkModeRegistry'
import {OrbitRound} from '@/model/resource/OrbitRound'

@Component({
  computed: {
    OrbitDistributeRewardStep() {
      return OrbitDistributeRewardStep
    },
  },
  components: {
    OrbitSuccessStep,
    OrbitLoadingTransactionStep,
    OrbitInsufficientGasStep,
    OrbitUnexpectedErrorStep,
    OrbitRejectedByTheUserStep,
  },
})
export default class OrbitDistributeRewardModal extends Vue {
  @Prop({type: Number, required: true}) roundNumber!: number

  currentStep: OrbitDistributeRewardStep | null = null
  transactionHash: string | null = null

  mounted() {
    this.populateResources()
  }

  async populateResources() {
    try {
      await OrbitHelper.checkWalletPermissions()
    } catch {
      this.closeModal()
      return
    }

    await this.distributeReward()
  }

  /**
   * After the chain tx succeeds, set finishedAt to "now" on the orbit_round row.
   * Uses list-by-round first; falls back to orbitRound embedded in loaded rewards.
   */
  private async persistRoundFinishedAt(
    rewards: OrbitRoundReward[],
    now: any
  ): Promise<void> {
    const roundsCol = new OrbitRoundCollection()
    roundsCol.round = this.roundNumber
    // Round numbers repeat across networks: without this it could finish the
    // other network's round of the same number.
    roundsCol.networkIdentifier = currentN3Network()
    roundsCol.limit = 1
    roundsCol.perPage = 1
    await roundsCol.queryAsPage()

    let roundFromDb = (roundsCol.items || []).find(
      r => Number(r.round) === Number(this.roundNumber)
    )

    if (!roundFromDb) {
      const fromReward = rewards.find(
        r =>
          r.orbitRound &&
          Number(r.orbitRound.round) === Number(this.roundNumber) &&
          r.orbitRound.idOrbitRoundPk > 0
      )
      if (fromReward?.orbitRound) {
        roundFromDb = fromReward.orbitRound
      }
    }

    if (!roundFromDb?.idOrbitRoundPk) {
      console.warn(
        'OrbitDistributeRewardModal: could not resolve orbit_round to set finishedAt'
      )
      return
    }

    const r = Object.assign(new OrbitRound(), roundFromDb)
    r.finishedAt = now
    await r.persistOrbitRound()
  }

  async loadRewardsForRound(): Promise<OrbitRoundReward[]> {
    const col = new OrbitRoundRewardCollection()
    col.round = this.roundNumber
    col.orderBy = 'idOrbitRoundRewardPk'
    col.asc = true
    col.limit = 10000
    col.perPage = 10000
    await col.queryAsPage()
    return col.items || []
  }

  async distributeReward() {
    try {
      const rewards = await this.loadRewardsForRound()

      const n3InvocationBuilder = getN3InvocationBuilder()
      const invocation = n3InvocationBuilder.distributeRewardInvocation({
        roundNumber: String(this.roundNumber),
      })

      this.currentStep = OrbitDistributeRewardStep.WAITING_SIGNATURE
      this.transactionHash = await $.walletAdapter.invokeN3Wallet(invocation)

      if (!this.transactionHash) throw new Error('Undefined transaction')

      this.currentStep = OrbitDistributeRewardStep.VALIDATING_TRANSACTION

      const isTransactionValid = await NeoHelper.validateTransaction(
        this.transactionHash
      )

      if (!isTransactionValid) throw new Error('Failed to verify transaction')

      const now = DateHelper.formatDateForBackend(Date.now()) as any
      await this.persistRoundFinishedAt(rewards, now)

      const distSuffix = '_distributed_to_others'
      await Promise.all(
        rewards.map(async (reward: OrbitRoundReward) => {
          const prev = reward.status?.trim()
          if (!prev || prev === 'claimed') return
          if (prev.endsWith(distSuffix)) return
          const r = Object.assign(new OrbitRoundReward(), reward)
          r.status = `${prev}${distSuffix}`
          r.updatedAt = now
          await r.persistOrbitRoundReward()
        })
      )

      this.currentStep = OrbitDistributeRewardStep.DISTRIBUTED

      $.toast.success(
        $.translate('view.ListOrbitRewardsView.distributeRewardSuccess')
      )
    } catch (error) {
      this.threatError(error)
    }
  }

  onClickCloseModal() {
    if (
      this.currentStep === OrbitDistributeRewardStep.WAITING_SIGNATURE ||
      this.currentStep === OrbitDistributeRewardStep.VALIDATING_TRANSACTION
    ) {
      return
    }

    this.closeModal()
  }

  closeModal() {
    this.currentStep = null
    this.transactionHash = null
    this.$emit('close')
  }

  private threatError(error: any) {
    console.error(error)

    const isNeonWalletRejected =
      error.message && String(error.message).includes('rejected by the user')

    const isNeoLineWalletRejected =
      error.description &&
      error.description === 'The user cancels, or refuses the dapps request'

    if (isNeonWalletRejected || isNeoLineWalletRejected) {
      this.currentStep = OrbitDistributeRewardStep.REJECTED_BY_THE_USER
      return
    }

    const hasInsufficientFunds =
      error.message && String(error.message).includes('insufficient funds')
    if (hasInsufficientFunds) {
      this.currentStep = OrbitDistributeRewardStep.INSUFFICIENT_GAS
      return
    }

    this.currentStep = OrbitDistributeRewardStep.UNKNOWN_ERROR
  }
}
